import React, { useState, useRef, useEffect, useMemo } from "react";
import EditHeader from "../EditHeader/EditHeader";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import styles from "../EditServicesOffered/EditServicesOffered.module.css";
import { useAgentCreator } from "../../hooks/useAgentCreator";
import { useNavigate } from "react-router-dom";
import PopUp from "../Popup/Popup";
import { useDashboardStore } from "../../Store/agentZustandStore";
import { businessServices } from "../../lib/businessServices";

const safeJsonParse = (value, fallback) => {
  try {
    if (!value || value === "undefined" || value === "null") return fallback;
    return JSON.parse(value);
  } catch (e) {
    return fallback;
  }
};

const EditServicesOffered = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [newService, setNewService] = useState("");
  const [isAddMoreChecked, setIsAddMoreChecked] = useState(false);

  const lastServiceRef = useRef(null);
  const listRef = useRef(null);

  const navigate = useNavigate();
  const agentnm = sessionStorage.getItem("agentName");
  const { setHasFetched } = useDashboardStore();

  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [popupMessage, setPopupMessage] = useState("");
  const [Loading, setLoading] = useState(null);

  const [customServices, setCustomServices] = useState([]);
  const [isEmptyListError, setEmptyListError] = useState("");

  // ✅ Safely parse sessionStorage
  const fetchBusinessType = safeJsonParse(
    sessionStorage.getItem("businessDetails"),
    {}
  );
  const fetchServices = safeJsonParse(
    sessionStorage.getItem("businesServices"),
    {}
  );

  const servicesFromSession = Array.isArray(fetchServices?.selectedService)
    ? fetchServices.selectedService
    : [];

  const [selectedService, setSelectedServices] = useState(servicesFromSession);

  const { handleCreateAgent } = useAgentCreator({
    stepValidator: () => "EditServicesOffered",
    setLoading,
    setPopupMessage,
    setPopupType,
    setShowPopup,
    navigate,
    setHasFetched,
  });

  // ✅ Track initial services to enable/disable Save
  const [initialServices, setInitialServices] = useState([]);
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    setInitialServices(servicesFromSession || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initial = [...(initialServices || [])].sort();
    const current = [...(selectedService || [])].sort();

    const sameLength = initial.length === current.length;
    const isSame = sameLength && initial.every((val, idx) => val === current[idx]);

    setIsChanged(!isSame);
  }, [selectedService, initialServices]);

  // ✅ Business type + safe matching
  const businessType = (fetchBusinessType?.businessType || "").trim();
  const isOther = businessType.toLowerCase() === "other";

  // ✅ EXACT match (not includes) so you don’t get false matches
  const filteredBusinessType = useMemo(() => {
    if (!businessType) return undefined;
    return businessServices?.find(
      (item) => (item?.type || "").toLowerCase() === businessType.toLowerCase()
    );
  }, [businessType]);
  const fallbackIcon = "/images/general-receptionist.png"; 
  const icon = filteredBusinessType?.icon || fallbackIcon;

  // ✅ default services (skip for Other)
  const defaultServices = useMemo(() => {
    if (isOther) return [];
    return (filteredBusinessType?.services || []).filter((s) => s !== "Other");
  }, [filteredBusinessType, isOther]);

  // ✅ Build list safely (no crashes)
  const uniqueServices = useMemo(() => {
    const combined = [
      ...defaultServices,
      ...(servicesFromSession || []),
      ...(customServices || []),
    ]
      .filter(Boolean)
      .filter((s) => s !== "Other");

    return Array.from(new Set(combined));
  }, [defaultServices, servicesFromSession, customServices]);

  const filteredServicesBySearch = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return uniqueServices;
    return uniqueServices.filter((s) => s.toLowerCase().includes(term));
  }, [uniqueServices, searchTerm]);

  const handleAddService = () => {
    const trimmedService = newService.trim();
    if (!trimmedService) return;

    const alreadySelected = selectedService.some(
      (s) => s.toLowerCase() === trimmedService.toLowerCase()
    );

    if (!alreadySelected) {
      setSelectedServices((prev) => [...prev, trimmedService]);
      setCustomServices((prev) => [...prev, trimmedService]);
      setNewService("");

      setTimeout(() => {
        lastServiceRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  const scrollListIntoView = () => {
    if (listRef.current) {
      listRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleContinue = async () => {
    if (Loading) return;

    if (!selectedService || selectedService.length === 0) {
      setEmptyListError("Service List Can not be empty");
      return;
    } else {
      setEmptyListError("");
    }

    // ✅ Update businesServices
    const previous = safeJsonParse(sessionStorage.getItem("businesServices"), {});
    const updatedBusinessServices = {
      ...previous,
      selectedService,
    };
    sessionStorage.setItem(
      "businesServices",
      JSON.stringify(updatedBusinessServices)
    );

    // ✅ Also update businessDetails
    const businessDetails = safeJsonParse(
      sessionStorage.getItem("businessDetails"),
      {}
    );
    const finalBusinessDetails = {
      ...businessDetails,
      selectedService,
    };
    sessionStorage.setItem("businessDetails", JSON.stringify(finalBusinessDetails));
    sessionStorage.setItem("selectedServices", JSON.stringify(selectedService));

    try {
      handleCreateAgent();
    } catch (error) {
      console.log("Agent Updation Hook failed", error);
    }
  };

  return (
    <>
      <EditHeader title="Edit Agent " agentName={agentnm} />
      <div className={styles.Maindiv}>
        <div className={styles.headerWrapper}>
          <h2 className={styles.heading}>Services Offered</h2>
          <p className={styles.subheading}>
            Select the “Services You Offer” based on the Business Category
          </p>

          {/* Optional helper for Other */}
          {isOther && (
            <p className={styles.subheading} style={{ marginTop: 6 }}>
              You selected <b>Other</b>. Add your services manually below.
            </p>
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.searchBox} ref={listRef}>
            <span className={styles.searchIcon}>
              <img src="svg/Search-Icon.svg" alt="Search icon" />
            </span>

            <input
              type="text"
              placeholder="Quick find service"
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setTimeout(scrollListIntoView, 300)}
              onClick={() => setTimeout(scrollListIntoView, 300)}
            />
          </div>

          <div className={styles.ListDiv}>
            <div className={styles.optionList}>
              {filteredServicesBySearch?.length > 0 ? (
                filteredServicesBySearch.map((item, index) => (
                  <label
                    className={styles.option}
                    key={`${item}-${index}`}
                    ref={
                      index === filteredServicesBySearch.length - 1
                        ? lastServiceRef
                        : null
                    }
                  >
                    <div className={styles.forflex}>
                      <div className={styles.icon}>
                        <img
                          src={icon}
                          alt={`${item} icon`}
                          className={styles.iconImg}
                        />
                      </div>
                      <div className={styles.strongDiv}>
                        <strong>{item}</strong>
                      </div>
                    </div>

                    <div>
                      <input
                        type="checkbox"
                        checked={selectedService.includes(item)}
                        onChange={() => {
                          if (selectedService.length >= 0) setEmptyListError("");
                          if (selectedService.includes(item)) {
                            setSelectedServices(selectedService.filter((s) => s !== item));
                          } else {
                            setSelectedServices([...selectedService, item]);
                          }
                        }}
                        className={styles.purpleCheckbox}
                      />
                    </div>
                  </label>
                ))
              ) : (
                <div className={styles.noResult}>No results found</div>
              )}
            </div>
          </div>

          {isEmptyListError && (
            <span style={{ color: "Red" }}>{isEmptyListError}</span>
          )}

          <div className={styles.addMore}>
            <input
              type="checkbox"
              id="addMoreServices"
              checked={isAddMoreChecked}
              className={styles.purpleCheckbox}
              onChange={() => setIsAddMoreChecked((prev) => !prev)}
            />
            <label htmlFor="addMoreServices">
              <p>Add More Services(Not on Above List)</p>
            </label>
          </div>

          {isAddMoreChecked && (
            <div className={styles.wrapper}>
              <label className={styles.label}>Service Name</label>
              <div className={styles.inputContainer}>
                <input
                  type="text"
                  placeholder="Enter your service name"
                  className={styles.input}
                  value={newService}
                  onChange={(e) => setNewService(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddService();
                  }}
                />
                <button
                  className={styles.addButton}
                  type="button"
                  onClick={handleAddService}
                >
                  <img src="/svg/addMore-icon.svg" alt="addMore-icon" />
                </button>
              </div>
            </div>
          )}

          <div
            className={styles.stickyWrapper}
            style={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "white",
              padding: "8px",
              opacity: isChanged ? 1 : 0.5,
              cursor: isChanged ? "pointer" : "not-allowed",
            }}
            onClick={isChanged ? handleContinue : undefined}
          >
            <AnimatedButton
              label="Save"
              isLoading={Loading}
              position={{ position: "relative" }}
              disabled={!isChanged}
            />
          </div>
        </div>

        {showPopup && (
          <PopUp
            type={popupType}
            onClose={() => {
              setShowPopup(false);
              navigate(-1);
            }}
            message={popupMessage}
            onConfirm={() => navigate("/edit-services-offered")}
          />
        )}
      </div>
    </>
  );
};

export default EditServicesOffered;
